"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUserLanguagePreferences } from "@/hooks/use-user-language-preferences";
import { resolveLocaleChangeHref } from "@/lib/i18n/navigation";
import { useUser } from "@clerk/nextjs";
import { Check, Globe } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { startTransition } from "react";
import { useTranslation } from "react-i18next";

export default function LanguageDropdown() {
  const { i18n, t } = useTranslation();
  const { updatePreferences } = useUserLanguagePreferences();
  const { isSignedIn } = useUser();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lang = i18n.language as "en" | "es";

  const handleChange = async (val: "en" | "es") => {
    if (lang === val) {
      return;
    }

    try {
      await i18n.changeLanguage(val);

      if (isSignedIn) {
        void updatePreferences({ appLocale: val }).catch(() => {
          // Public locale switching should still work if persistence fails.
        });
      }

      const nextHref = resolveLocaleChangeHref(
        pathname,
        val,
        searchParams.toString()
      );
      const currentHref = `${pathname}${
        searchParams.size ? `?${searchParams.toString()}` : ""
      }`;

      startTransition(() => {
        if (nextHref === currentHref) {
          router.refresh();
          return;
        }

        router.replace(nextHref, { scroll: false });
      });
    } catch {
      // The menu has no inline error state; preserving the current locale is safer.
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-auto gap-1.5 rounded-full px-2.5 text-gray-700 transition-colors duration-200 dark:text-gray-200"
        >
          <Globe aria-hidden="true" className="h-4 w-4" />
          <span aria-hidden="true" className="text-xs font-semibold tracking-wide">
            {lang.toUpperCase()}
          </span>
          <span className="sr-only">{t("NAV_CHANGE_LANGUAGE_LABEL")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="min-w-24 rounded-xl border-slate-200/80 bg-white/95 shadow-lg duration-200 ease-out dark:border-white/10 dark:bg-slate-950/95 [&_[role=menuitem]]:min-h-9 [&_[role=menuitem]]:justify-between"
      >
        <DropdownMenuItem onClick={() => handleChange("en")}>
          EN {lang === "en" && <Check aria-hidden="true" className="h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleChange("es")}>
          ES {lang === "es" && <Check aria-hidden="true" className="h-4 w-4" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
